import { useEffect, useState } from 'react';
import { createICSpicyActor } from '../services/icspicy';
import { HttpAgent } from '@dfinity/agent';

interface MultichainDeployment {
  token_id: bigint;
  evm?: {
    contract_address: string;
    chain_id: number;
    transaction_hash?: string;
    status: { Deploying?: null; Deployed?: null; Failed?: null };
    deployed_at?: bigint;
  };
  solana?: {
    mint_address: string;
    transaction_signature?: string;
    status: { Deploying?: null; Deployed?: null; Failed?: null };
    deployed_at?: bigint;
  };
  bitcoin?: {
    inscription_id: string;
    transaction_id?: string;
    status: { Deploying?: null; Deployed?: null; Failed?: null };
    deployed_at?: bigint;
  };
}

interface MultichainStatusProps {
  tokenId: bigint;
  agent: HttpAgent | null;
  onRetry?: (chain: 'evm' | 'solana' | 'bitcoin') => void;
}

export default function MultichainStatus({ tokenId, agent, onRetry }: MultichainStatusProps) {
  const [deployment, setDeployment] = useState<MultichainDeployment | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDeployment = async () => {
      if (!agent) {
        setLoading(false);
        return;
      }

      try {
        const actor = await createICSpicyActor(agent);
        const result = await actor.get_multichain_deployment(tokenId);
        setDeployment(result || null);
      } catch (err) {
        console.error('Failed to load deployment status:', err);
      } finally {
        setLoading(false);
      }
    };

    loadDeployment();
    
    // Poll for updates every 5 seconds if deployment is in progress
    const interval = setInterval(() => {
      if (deployment && (
        deployment.evm?.status.Deploying ||
        deployment.solana?.status.Deploying ||
        deployment.bitcoin?.status.Deploying
      )) {
        loadDeployment();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [agent, tokenId, deployment]);

  if (loading) {
    return (
      <div className="p-4 bg-black/30 rounded-lg">
        <p className="text-gray-300">Loading deployment status...</p>
      </div>
    );
  }

  const getStatusColor = (status: { Deploying?: null; Deployed?: null; Failed?: null }) => {
    if (status.Deploying) return 'text-yellow-400';
    if (status.Deployed) return 'text-green-400';
    if (status.Failed) return 'text-red-400';
    return 'text-gray-400';
  };

  const getStatusText = (status: { Deploying?: null; Deployed?: null; Failed?: null }) => {
    if (status.Deploying) return '⏳ Deploying';
    if (status.Deployed) return '✓ Deployed';
    if (status.Failed) return '✗ Failed';
    return 'Not Started';
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
        <span className="text-gray-300 font-semibold">ICP (Native)</span>
        <span className="text-green-400">✓ Deployed</span>
      </div>
      
      {deployment?.evm && (
        <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-gray-300 font-semibold">Ethereum</span>
              <span className={getStatusColor(deployment.evm.status)}>
                {getStatusText(deployment.evm.status)}
              </span>
            </div>
            <p className="text-xs text-gray-400 font-mono break-all">{deployment.evm.contract_address}</p>
            {deployment.evm.status.Failed && onRetry && (
              <button
                onClick={() => onRetry('evm')}
                className="mt-2 text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
              >
                Retry
              </button>
            )}
          </div>
        </div>
      )}
      
      {deployment?.solana && (
        <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-gray-300 font-semibold">Solana</span>
              <span className={getStatusColor(deployment.solana.status)}>
                {getStatusText(deployment.solana.status)}
              </span>
            </div>
            <p className="text-xs text-gray-400 font-mono break-all">{deployment.solana.mint_address}</p>
            {deployment.solana.status.Failed && onRetry && (
              <button
                onClick={() => onRetry('solana')}
                className="mt-2 text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
              >
                Retry
              </button>
            )}
          </div>
        </div>
      )}
      
      {deployment?.bitcoin && (
        <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-gray-300 font-semibold">Bitcoin</span>
              <span className={getStatusColor(deployment.bitcoin.status)}>
                {getStatusText(deployment.bitcoin.status)}
              </span>
            </div>
            <p className="text-xs text-gray-400 font-mono break-all">{deployment.bitcoin.inscription_id}</p>
            {deployment.bitcoin.status.Failed && onRetry && (
              <button
                onClick={() => onRetry('bitcoin')}
                className="mt-2 text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
              >
                Retry
              </button>
            )}
          </div>
        </div>
      )}
      
      {!deployment && (
        <p className="text-gray-400 text-sm">No multichain deployment found for this NFT.</p>
      )}
    </div>
  );
}

